import { useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Col,
  Flex,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Row
} from 'antd'
import { ModalProps } from 'antd/lib'
import { useEffect } from 'react'

import {
  useClassifierServiceCreateTreatmentRegimenTypeEndpoint,
  useClassifierServiceGetTreatmentRegimenTypeEndpoint,
  useClassifierServiceSearchTreatmentRegimenTypeEndpointKey,
  useClassifierServiceUpdateTreatmentRegimenTypeEndpoint
} from 'src/api/queries'
import { ApiError, ClassifierCategory, ClassifierType } from 'src/api/requests'
import { LANGUAGE_KEYS, requiredRules } from 'src/app-constants'
import {
  ClassifierInfiniteSelect,
  EditableFormItem,
  SaveConfirm
} from 'src/components'
import { useClassifierId } from 'src/hooks'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

type Props = {
  id?: string
  onCancel: () => void
} & ModalProps

export const TreatmentTypesClassifierModal = ({
  id,
  onCancel,
  ...props
}: Props) => {
  const { t } = useLocales()
  const queryClient = useQueryClient()

  const [form] = Form.useForm()

  const treatmentTypes = useClassifierId(
    ClassifierType.TREATMENT_TYPE,
    ClassifierCategory.TREATMENT
  )

  const { data, isLoading } =
    useClassifierServiceGetTreatmentRegimenTypeEndpoint(
      {
        id: String(id)
      },
      [id],
      { enabled: !!id }
    )

  const { mutate: create } =
    useClassifierServiceCreateTreatmentRegimenTypeEndpoint({
      onError: (err: ApiError) => formErrorHandler(err.body.errors),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [useClassifierServiceSearchTreatmentRegimenTypeEndpointKey]
        })
        message.success(
          t('treatment_types_classifier.create_success_treatment_type')
        )
        onCancel()
      }
    })

  const { mutate, isPending } =
    useClassifierServiceUpdateTreatmentRegimenTypeEndpoint({
      onError: (err: ApiError) => formErrorHandler(err.body.errors),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [useClassifierServiceSearchTreatmentRegimenTypeEndpointKey]
        })
        message.success(
          t('treatment_types_classifier.edit_success_treatment_type')
        )
        onCancel()
      }
    })

  const onSave = async () => {
    const values = await form.validateFields()
    const payload = {
      typeId: values?.typeId,
      comments: values?.comments,
      order: values?.order,
      translation: {
        ro: values?.translation?.ro,
        ru: values?.translation?.ru
      }
    }

    id
      ? mutate({ id: String(id), requestBody: payload })
      : create({
          requestBody: payload
        })
  }

  useEffect(() => {
    if (data)
      form.setFieldsValue({
        typeId: data?.type?.id,
        comments: data?.comments,
        order: data?.order,
        translation: {
          ro: data?.translation?.ro,
          ru: data?.translation?.ru
        }
      })
  }, [data])

  return (
    <Modal
      open
      centered
      loading={isLoading}
      maskClosable={false}
      width={700}
      footer={null}
      title={t(
        id ? 'common.edit' : 'treatment_types_classifier.create_treatment_type'
      )}
      onCancel={onCancel}
      {...props}
    >
      <Form layout="vertical" requiredMark={false} form={form}>
        <Row gutter={[24, 24]}>
          {LANGUAGE_KEYS.map((langKey) => {
            return (
              <Col span={12} key={langKey}>
                <EditableFormItem
                  isEditing={true}
                  name={['translation', langKey]}
                  label={`${langKey.toUpperCase()}`}
                  rules={requiredRules}
                  className="full-width"
                >
                  <Input placeholder={t('common.enter_text')} />
                </EditableFormItem>
              </Col>
            )
          })}

          <Col span={12}>
            <Form.Item name="order" label={t('common.order')}>
              <InputNumber
                className="full-width"
                placeholder={t('common.order')}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="typeId"
              label={t('treatment_types_classifier.treatment_type')}
            >
              <ClassifierInfiniteSelect
                id={treatmentTypes}
                placeholder={t('treatment_types_classifier.treatment_type')}
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="comments" label={t('common.comments')}>
              <Input.TextArea rows={5} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Flex justify="center" gap={8}>
              <Button onClick={onCancel}>{t('common.cancel')}</Button>

              <SaveConfirm
                title={`${t('common.save')}?`}
                onConfirm={onSave}
                okButtonProps={{ loading: isPending }}
              >
                <Button type="primary">{t('common.save')}</Button>
              </SaveConfirm>
            </Flex>
          </Col>
        </Row>
      </Form>
    </Modal>
  )
}
